import { SortingState } from "@tanstack/react-table";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";

type SortValue = string | number | boolean | null | undefined;

export function formatGroupType(type?: string) {
    if (!type) return "";
    if (type === "service provider") return "CSP";
    return type.charAt(0).toUpperCase() + type.slice(1);
}

export function formatAccountType(type?: string) {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
}

export const getOrgName = (org: User["organization"]): string =>
    typeof org === "string" ? org : (org?.name ?? "");

function compareValues(a: SortValue, b: SortValue) {
    if (a === b) return 0;

    if (typeof a === "boolean" && typeof b === "boolean") {
        return Number(a) - Number(b);
    }

    return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
    });
}

function getOrganizationSortValue(organization: Organization, columnId: string) {
    if (columnId === "group_type") {
        return organization.group_type ?? "";
    }

    if (columnId === "service_provider") {
        return organization.service_provider?.name ?? "";
    }

    return organization.name ?? "";
}

function getUserSortValue(user: User, columnId: string) {
    if (columnId === "email") {
        return user.username ?? "";
    }

    if (columnId === "organization") {
        return getOrgName(user.organization);
    }

    if (columnId === "account_type") {
        return user.account_type ?? "";
    }

    if (columnId === "status") {
        return user.is_active;
    }

    return user.full_name ?? "";
}

export function sortOrganizations(
    organizations: Organization[],
    sorting: SortingState,
) {
    const sort = sorting[0];

    if (!sort) {
        return organizations;
    }

    return [...organizations].sort((a, b) => {
        const result = compareValues(
            getOrganizationSortValue(a, sort.id),
            getOrganizationSortValue(b, sort.id),
        );

        return sort.desc ? -result : result;
    });
}

export function sortUsers(users: User[], sorting: SortingState) {
    const sort = sorting[0];

    if (!sort) {
        return users;
    }

    return [...users].sort((a, b) => {
        const result = compareValues(
            getUserSortValue(a, sort.id),
            getUserSortValue(b, sort.id),
        );

        return sort.desc ? -result : result;
    });
}